import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  OnChanges,
  OnInit,
  SimpleChanges,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MAT_DIALOG_DATA,
} from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { SpinnerComponent } from '../../spinner/spinner.component';

interface SimpleDialogData {
  title?: string;
  content?: string | string[];
  additionalContent?: Observable<string>;
  confirmMessage?: string;
  cancelMessage?: string;
  warning?: boolean;
  closed?: boolean;
  errorEvent?: EventEmitter<string>;
}

const DEFAULT_DATA: SimpleDialogData = {};

@Component({
  selector: 'app-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  styles: [
    `
      ul {
        margin-top: 0;
      }
      .buttons {
        display: block;
        text-align: end;
      }
      button {
        margin-left: 5px;
      }
      .spacer {
        width: 377px;
        height: 100px;
      }
      .content {
        padding: 5px 0;
      }
    `,
  ],
  imports: [
    MatDialogTitle,
    SpinnerComponent,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmDialogComponent implements OnInit, OnChanges {
  private dialogRef = inject<MatDialogRef<ConfirmDialogComponent>>(
    MatDialogRef,
    {
      optional: true,
    },
  );
  private dialogData = inject<SimpleDialogData>(MAT_DIALOG_DATA, {
    optional: true,
  });

  // Input properties for use as a child component
  readonly title = input<string>();
  readonly content = input<string | string[]>();
  readonly confirmMessage = input<string>();
  readonly cancelMessage = input<string>();
  readonly warning = input<boolean>(false);

  // Merged data from inputs or MAT_DIALOG_DATA
  protected get data(): SimpleDialogData {
    return {
      title: this.title() ?? this.dialogData?.title,
      content: this.content() ?? this.dialogData?.content,
      confirmMessage: this.confirmMessage() ?? this.dialogData?.confirmMessage,
      cancelMessage: this.cancelMessage() ?? this.dialogData?.cancelMessage,
      warning: this.warning() ?? this.dialogData?.warning,
      additionalContent: this.dialogData?.additionalContent,
      closed: this.dialogData?.closed,
      errorEvent: this.dialogData?.errorEvent,
    };
  }

  confirmed = false;
  readonly loading = signal(false);
  isList = false;
  readonly errorMessage = signal<string>(undefined);

  readonly dialogConfirmed = output<boolean>();

  close() {
    this.dialogRef?.close();
    this.dialogConfirmed.emit(false);
  }

  confirm() {
    if (this.data.closed || !this.data.additionalContent) {
      this.dialogRef?.close(true);
      this.dialogConfirmed.emit(true);
    } else {
      this.dialogConfirmed.emit(true);
      this.confirmed = true;
      this.loading.set(true);
    }
  }

  ngOnInit() {
    const additionalContent = this.data.additionalContent;
    if (additionalContent) {
      additionalContent.subscribe(() => {
        this.loading.set(false);
      });
    }
    const errorEvent = this.data.errorEvent;
    if (errorEvent) {
      errorEvent.subscribe((errorMsg) => {
        this.errorMessage.set(errorMsg);
        this.loading.set(false);
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ('content' in changes) {
      this.isList = Array.isArray(this.data.content);
    }
  }
}
